import React, { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, CloudUpload, Flag, Loader2, MonitorSmartphone, Pause, Play, RotateCcw, Square,
} from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import { RegistrazioneVerbale, TESTO_ALTRA_SCHEDA } from '../../hooks/useRegistrazioneVerbale';
import { MAX_ETICHETTA_MOMENTO } from '../../services/verbaliRegistratore';
import { formattaMs } from '../../utils/verbaliTrascrizione';
import {
  oraMinuti, testoBlocchiInAttesa, testoInterrotta, TESTO_IN_PAUSA, TESTO_IN_REGISTRAZIONE, TESTO_SOLO_IN_MEMORIA,
  TESTO_TELEFONO_FERMO,
} from './testiAudio';

interface VerbaleRegistrazioneProps {
  registrazione: RegistrazioneVerbale;
  /** Punti dell'ordine del giorno, per «Segna momento» → «Inizio punto N». */
  odg: string[];
}

/** Registra, è in pausa o sta chiudendo: la pagina blocca la navigazione interna (Task 14). */
export const registrazioneInCorso = (r: RegistrazioneVerbale) =>
  r.stato === 'registra' || r.stato === 'pausa' || r.terminando;

/** Il pannello ha qualcosa da mostrare (altrimenti non disegna nulla). */
export const pannelloRegistrazioneVisibile = (r: RegistrazioneVerbale) =>
  registrazioneInCorso(r) || !!r.interrotta || r.altraScheda || !!r.errore
  || r.avvisi.length > 0 || r.inAttesa > 0 || r.soloInMemoria;

const TACCHE = 14;

/** Barra del livello del microfono a tacche (0..1). */
const Livello: React.FC<{ livello: number; attivo: boolean }> = ({ livello, attivo }) => {
  const valore = Math.round(Math.max(0, Math.min(1, livello || 0)) * 100);
  const accese = attivo ? Math.round((valore / 100) * TACCHE) : 0;
  return (
    <div
      role="meter"
      aria-label="Livello del microfono"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={valore}
      className="flex h-4 items-end gap-0.5"
    >
      {Array.from({ length: TACCHE }, (_, i) => (
        <span
          key={i}
          className={`w-1.5 rounded-sm transition-colors ${
            i < accese
              ? (i >= TACCHE - 2 ? 'bg-amber-500' : 'bg-emerald-500')
              : 'bg-slate-200 dark:bg-gray-600'
          }`}
          style={{ height: `${40 + (i / (TACCHE - 1)) * 60}%` }}
        />
      ))}
    </div>
  );
};

/** «Segna momento»: punti dell'OdG («Inizio punto N») o un'etichetta libera. */
const SegnaMomento: React.FC<{ odg: string[]; disabilitato: boolean; onSegna: (etichetta: string) => void }> = ({
  odg, disabilitato, onSegna,
}) => {
  const [aperto, setAperto] = useState(false);
  const [libera, setLibera] = useState('');
  const contenitoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!aperto) return undefined;
    const fuori = (e: MouseEvent) => {
      if (contenitoreRef.current && !contenitoreRef.current.contains(e.target as Node)) setAperto(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAperto(false); };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', esc);
    };
  }, [aperto]);

  const segna = (etichetta: string) => {
    const pulita = etichetta.trim().slice(0, MAX_ETICHETTA_MOMENTO);
    if (!pulita) return;
    onSegna(pulita);
    setLibera('');
    setAperto(false);
  };

  return (
    <div className="relative" ref={contenitoreRef}>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        disabled={disabilitato}
        aria-haspopup="menu"
        aria-expanded={aperto}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
      >
        <Flag className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        Segna momento
      </button>
      {aperto && (
        <div className="absolute left-0 z-20 mt-2 w-72 max-w-[calc(100vw-3rem)] rounded-xl border border-slate-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {odg.length > 0 && (
            <ul role="menu" aria-label="Punti dell'ordine del giorno" className="max-h-56 overflow-y-auto">
              {odg.map((punto, i) => (
                <li key={`${i}-${punto}`} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => segna(`Inizio punto ${i + 1}: ${punto}`)}
                    className="flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-slate-700 hover:bg-emerald-50 dark:text-gray-200 dark:hover:bg-emerald-900/30"
                  >
                    <span className="flex-shrink-0 font-semibold text-emerald-700 dark:text-emerald-300">Inizio punto {i + 1}</span>
                    <span className="min-w-0 truncate text-slate-500 dark:text-gray-400">{punto}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form
            className={`flex gap-1.5 ${odg.length > 0 ? 'mt-2 border-t border-slate-100 pt-2 dark:border-gray-700' : ''}`}
            onSubmit={(e) => { e.preventDefault(); segna(libera); }}
          >
            <input
              type="text"
              value={libera}
              onChange={(e) => setLibera(e.target.value)}
              maxLength={MAX_ETICHETTA_MOMENTO}
              placeholder="Es. votazione sul bilancio"
              aria-label="Etichetta del momento"
              className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            <button
              type="submit"
              disabled={!libera.trim()}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Segna
            </button>
          </form>
          <p className="mt-1.5 px-1 text-xs text-slate-500 dark:text-gray-400">
            I momenti aiutano l'AI a dividere lo svolgimento della riunione.
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Pannello della registrazione in corso (stato, cronometro, livello, Pausa/Riprendi, «Segna momento»,
 * «Termina»), della chiusura e della ripresa dopo un'interruzione («Riprendi» / «Termina qui»).
 * Non disegna nulla quando non c'è niente da mostrare (`pannelloRegistrazioneVisibile`).
 */
const VerbaleRegistrazione: React.FC<VerbaleRegistrazioneProps> = ({ registrazione: r, odg }) => {
  const [conferma, setConferma] = useState<'termina' | 'terminaQui' | null>(null);
  const [ultimo, setUltimo] = useState<{ etichetta: string; ms: number } | null>(null);

  const attiva = r.stato === 'registra' || r.stato === 'pausa';
  const inPausa = r.stato === 'pausa';
  const interrotta = !attiva && !r.altraScheda ? r.interrotta : null;

  if (!pannelloRegistrazioneVisibile(r)) return null;

  const segna = (etichetta: string) => {
    r.segnaMomento(etichetta);
    setUltimo({ etichetta, ms: r.msRipresa });
  };

  return (
    <section
      aria-label="Registrazione"
      className={`rounded-2xl border bg-white p-4 shadow-sm dark:bg-gray-800 ${
        attiva && !inPausa ? 'border-red-200 dark:border-red-900/60' : 'border-slate-200 dark:border-gray-700'
      }`}
    >
      {attiva && (
        <>
          <div className="flex items-center gap-3">
            <span className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center" aria-hidden="true">
              {!inPausa && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-60 motion-reduce:animate-none" />
              )}
              <span className={`relative inline-flex h-7 w-7 items-center justify-center rounded-full ${inPausa ? 'bg-amber-500' : 'bg-red-600'}`}>
                {inPausa && <Pause className="h-3.5 w-3.5 text-white" />}
              </span>
            </span>
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${inPausa ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300'}`}>
                {inPausa ? TESTO_IN_PAUSA : TESTO_IN_REGISTRAZIONE}
              </p>
              <p className="font-mono text-base font-semibold tabular-nums text-slate-900 dark:text-white" aria-live="off">
                {formattaMs(r.msRipresa)}
              </p>
            </div>
            <div className="ml-auto">
              <Livello livello={r.livello} attivo={!inPausa} />
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {inPausa ? (
              <button
                type="button"
                onClick={r.riprendi}
                disabled={r.terminando}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Play className="h-4 w-4" aria-hidden="true" />
                Riprendi
              </button>
            ) : (
              <button
                type="button"
                onClick={r.pausa}
                disabled={r.terminando}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <Pause className="h-4 w-4" aria-hidden="true" />
                Pausa
              </button>
            )}
            <SegnaMomento odg={odg} disabilitato={r.terminando} onSegna={segna} />
            <button
              type="button"
              onClick={() => setConferma('termina')}
              disabled={r.terminando}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
            >
              <Square className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
              Termina
            </button>
          </div>

          {ultimo && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
              <Flag className="h-3 w-3 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
              Ultimo momento segnato: «{ultimo.etichetta}» a {formattaMs(ultimo.ms)}
            </p>
          )}
          <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">
            Tieni questa pagina aperta fino a «Termina»: ogni minuto di audio viene salvato e inviato.
          </p>
        </>
      )}

      {r.altraScheda && !attiva && (
        <div className="flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700 dark:bg-gray-700/50 dark:text-gray-200">
          <MonitorSmartphone className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          <span>{TESTO_ALTRA_SCHEDA}</span>
        </div>
      )}

      {interrotta && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-800/60 dark:bg-amber-900/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">{testoInterrotta(interrotta.alle)}</p>
              <p className="text-xs text-amber-800/90 dark:text-amber-200/80">
                L'audio registrato fin qui è al sicuro. Riprendi per continuare la stessa registrazione, oppure terminala qui.
              </p>
              {interrotta.dalTelefono && (
                <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-200/80">{TESTO_TELEFONO_FERMO}</p>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { r.riprendiInterrotta(); }}
              disabled={r.terminando}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <Play className="h-4 w-4" aria-hidden="true" />
              Riprendi
            </button>
            <button
              type="button"
              onClick={() => setConferma('terminaQui')}
              disabled={r.terminando}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Square className="h-3.5 w-3.5" aria-hidden="true" />
              Termina qui
            </button>
          </div>
        </div>
      )}

      {r.terminando && (
        <div className="mt-3 flex items-start gap-2 text-sm text-slate-700 dark:text-gray-200">
          <Loader2 className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          <span>{r.attesaRete ? r.testoAttesaRete : 'Chiusura della registrazione: invio degli ultimi blocchi…'}</span>
        </div>
      )}

      {r.inAttesa > 0 && !r.terminando && (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-gray-300">
          <CloudUpload className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          {testoBlocchiInAttesa(r.inAttesa)}
        </p>
      )}

      {r.soloInMemoria && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-900/20 dark:text-amber-200" role="alert">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {TESTO_SOLO_IN_MEMORIA}
        </p>
      )}

      {r.errore && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300" role="alert">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{r.errore}</span>
          {r.inAttesa > 0 && !r.terminando && (
            <button
              type="button"
              onClick={r.riprovaInvio}
              className="inline-flex flex-shrink-0 items-center gap-1 font-semibold text-red-800 underline-offset-2 hover:underline dark:text-red-200"
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" />
              Riprova l'invio
            </button>
          )}
        </div>
      )}

      {r.avvisi.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {r.avvisi.map((a) => (
            <li key={a} className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span>{a}</span>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={conferma === 'termina'}
        onClose={() => setConferma(null)}
        onConfirm={() => { r.termina(); }}
        title="Terminare la registrazione?"
        message="La registrazione si chiude e parte la trascrizione. Per registrare ancora servirà una nuova registrazione."
        confirmText="Termina"
        type="warning"
      />
      <ConfirmDialog
        isOpen={conferma === 'terminaQui'}
        onClose={() => setConferma(null)}
        onConfirm={() => { r.terminaInterrotta(); }}
        title="Terminare qui la registrazione?"
        message={`La registrazione si chiude con l'audio arrivato${r.interrotta ? ` fino alle ${oraMinuti(r.interrotta.alle)}` : ''} e parte la trascrizione: non si potrà più riprendere.`}
        confirmText="Termina qui"
        type="warning"
      />
    </section>
  );
};

export default VerbaleRegistrazione;
